import type {Ability} from '../../../../generated/types';
import {ABILITY_BY_SHORT_NAME, isAbility} from './abilities';
import {calculate, isArithmeticExpression} from './arithmetic';
import type {FormulaValue} from './types';

export type FormulaContext = {
	/** уровень класса-владельца особенности, а у вида, происхождения и черт — уровень персонажа */
	level: number;
	proficiencyBonus: number;
	/** скорость хождения для `{speed}` */
	speed: number;
	abilityModifier(ability: Ability): number;
	/** ячейка таблицы класса-владельца на его уровне */
	tableValue(column: string): string | undefined;
	/** значение выбора или переменной варианта по имени */
	variable(name: string): string | undefined;
};

const TOKEN = /\{([^{}]+)\}/g;
const SINGLE_TOKEN = /^\{([^{}]+)\}$/;
const DICE = /^\d*d\d+$/;

export function resolveReference(value: string, context: FormulaContext): string {
	const token = value.match(SINGLE_TOKEN)?.[1];
	if (!token) return value;
	return context.variable(token) ?? value;
}

export function evaluateFormula(value: FormulaValue | undefined, context: FormulaContext): FormulaValue | undefined {
	if (typeof value !== 'string') return value;
	const expression = value.replace(TOKEN, (token: string, name: string) =>
		String(evaluateToken(name, context) ?? token),
	);
	if (isArithmeticExpression(expression)) return calculate(expression) ?? expression;
	return combineDiceTerms(expression);
}

export function evaluateCount(
	value: FormulaValue | undefined,
	context: FormulaContext,
	min: number | undefined,
): FormulaValue | undefined {
	const result = evaluateFormula(value, context);
	return typeof result === 'number' && min != null ? Math.max(min, result) : result;
}

export function evaluateNumber(value: FormulaValue | undefined, context: FormulaContext): number | undefined {
	const result = evaluateFormula(value, context);
	if (typeof result === 'number') return result;
	const parsed = parseInt(result ?? '', 10);
	return Number.isNaN(parsed) ? undefined : parsed;
}

function evaluateToken(name: string, context: FormulaContext): FormulaValue | undefined {
	if (name === 'level') return context.level;
	if (name === 'proficiency') return context.proficiencyBonus;
	if (name === 'speed') return context.speed;
	if (ABILITY_BY_SHORT_NAME[name]) return context.abilityModifier(ABILITY_BY_SHORT_NAME[name]);
	if (name.startsWith('dice:')) return name.slice('dice:'.length);
	if (name.startsWith('table:')) return context.tableValue(name.slice('table:'.length));

	const variable = context.variable(name);
	return isAbility(variable) ? context.abilityModifier(variable) : variable;
}

function combineDiceTerms(expression: string): string {
	const terms = expression.split('+').map((term) => term.trim());
	const dice = terms.filter((term) => DICE.test(term));
	const numbers = terms.filter((term) => !DICE.test(term));
	const numericTotal = numbers.every(isArithmeticExpression)
		? numbers.reduce((total, term) => total + (calculate(term) ?? 0), 0)
		: undefined;

	if (numericTotal === undefined || !dice.length) return expression;
	if (!numericTotal) return dice.join(' + ');
	const sign = numericTotal > 0 ? '+' : '−';
	return `${dice.join(' + ')} ${sign} ${Math.abs(numericTotal)}`;
}
